import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWalletContext } from '../context/WalletContext';
import { 
  fetchGatewayRewards,
  RewardsGraphDataItem,
  processRewardsForGraph,
  GatewayRewardsData
} from '../services/RewardsService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BoltIcon } from '@heroicons/react/24/solid';
import { useGateways } from '../context/GatewayContext';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import PageContainer from '../components/layout/PageContainer';

// Add new interface for selected gateway details
interface SelectedGatewayDetails {
  gwId: string;
  name: string;
  date: string;
  derDetails: {
    [sn: string]: number;
  };
}

// Define time period options
enum TimePeriod {
  ALL_TIME = 'ALL_TIME',
  THIS_MONTH = 'THIS_MONTH',
  LAST_MONTH = 'LAST_MONTH'
}

// Interface for gateway rewards structure
interface GatewayReward {
  gwId: string;
  name: string;
  amount: number;
}

const RewardsPage: React.FC = (): JSX.Element => {
  const { publicKey } = useWalletContext();
  const { gateways } = useGateways();
  const [totalRewards, setTotalRewards] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userGatewayRewards, setUserGatewayRewards] = useState<GatewayReward[]>([]);
  const [combinedDailyRewards, setCombinedDailyRewards] = useState<{
    isLoading: boolean;
    error: string | null;
    data: Record<string, RewardsGraphDataItem[]>;
  }>({
    isLoading: false,
    error: null,
    data: {}
  });
  const [selectedGatewayDetails, setSelectedGatewayDetails] = useState<SelectedGatewayDetails | null>(null);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriod>(TimePeriod.ALL_TIME);
  
  // Store generated sample data to keep it consistent between renders
  const sampleDataRef = useRef<RewardsGraphDataItem[] | null>(null);
  
  // Store whether the initial data has been loaded
  const initialDataLoadedRef = useRef<boolean>(false);

  // Add a ref to store the rewards data for filtering
  const rewardsDataRef = useRef<GatewayRewardsData | null>(null);

  // Clear sample data when wallet changes
  useEffect(() => {
    // Reset sample data cache when wallet changes
    sampleDataRef.current = null;
    initialDataLoadedRef.current = false;
  }, [publicKey]);

  // Move generateSampleData to useCallback
  const generateSampleData = useCallback((): RewardsGraphDataItem[] => {
    // Return cached sample data if already generated
    if (sampleDataRef.current) {
      return sampleDataRef.current;
    }
    
    // Generate new sample data with deterministic seed based on wallet address
    const seed = publicKey ? publicKey.toString().slice(0, 8) : 'default-seed';
    const seedValue = Array.from(seed).reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    const today = new Date();
    const sampleData: RewardsGraphDataItem[] = [];
    
    // Generate 30 days of sample data
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Format date as ISO string with time part set to 00:00:00
      const dateStr = `${date.toISOString().split('T')[0]}T00:00:00`;
      
      // Generate pseudo-random rewards using a seed based on publicKey and day
      // This ensures same wallet + same day = same rewards value
      const daySeed = seedValue + i;
      const randomRewards = 5000 + (daySeed * 829 % 25000);
      
      sampleData.push({
        date: dateStr,
        rewards: randomRewards,
        wallet: publicKey?.toString() || '',
        gwId: 'sample-gateway',
        derDetails: { 'sample-der': randomRewards },
        isSampleData: true // Add flag to indicate this is sample data
      });
    }
    
    // Cache the generated sample data
    sampleDataRef.current = sampleData;
    
    return sampleData;
  }, [publicKey]);

  // Main data fetching effect
  useEffect(() => {
    // Skip if no wallet is connected
    if (!publicKey) {
      setTotalRewards(null);
      setError(null);
      setCombinedDailyRewards({
        isLoading: false,
        error: null,
        data: {}
      });
      return;
    }

    const fetchWalletData = async () => {
      setIsLoading(true);
      setCombinedDailyRewards(prev => ({ ...prev, isLoading: true }));
      
      try {
        const walletAddress = publicKey.toString();
        console.log('Fetching rewards for wallet:', walletAddress.slice(0, 8) + '...');
        
        // Use the gwId parameter as an empty string to get all rewards for this wallet
        const data = await fetchGatewayRewards('', walletAddress);
        
        // Store the full rewards data for filtering
        rewardsDataRef.current = data;
        
        console.log('Raw API response data:', {
          data,
          hasBetaInfo: !!data?.betaInfo,
          betaInfoTotal: data?.betaInfo?.total,
          hasBetaDaily: !!data?.betaDaily,
          betaDailyLength: data?.betaDaily?.length || 0
        });

        // Set total rewards from betaInfo
        if (data?.betaInfo?.total !== undefined) {
          console.log('Setting total rewards from betaInfo:', data.betaInfo.total);
          setTotalRewards(data.betaInfo.total);
          setError(null);
        } else if (data?.betaDaily && data.betaDaily.length > 0) {
          // If betaInfo.total is undefined but we have betaDaily data,
          // calculate the total from daily rewards
          let calculatedTotal = 0;
          data.betaDaily.forEach(gateway => {
            if (gateway.rewards && gateway.rewards.length > 0) {
              gateway.rewards.forEach(reward => {
                if (reward.wallet === walletAddress) {
                  calculatedTotal += reward.amount;
                }
              });
            }
          });
          console.log('Calculated total from betaDaily:', calculatedTotal);
          setTotalRewards(calculatedTotal);
          setError(null);
        } else {
          console.log('No rewards data available');
          setTotalRewards(0);
          setError('No rewards data available');
        }

        // Process daily rewards data
        if (data?.betaDaily) {
          console.log('Processing betaDaily data for rewards');
          const rewardsData = processRewardsForGraph(data);
          // Always use real data, even if it's empty
          console.log('Using real rewards data for chart:', rewardsData.length, 'data points');
          sampleDataRef.current = rewardsData;
          setCombinedDailyRewards({
            isLoading: false,
            error: null,
            data: { 'total': rewardsData }
          });
        } else {
          console.log('No betaDaily data available, using sample data');
          const sampleData = generateSampleData();
          sampleDataRef.current = sampleData;
          setCombinedDailyRewards({
            isLoading: false,
            error: null,
            data: { 'total': sampleData }
          });
        }

        // Process gateway rewards
        if (data?.betaDaily) {
          const gatewayIds = [...new Set(data.betaDaily.map(item => item.gwId).filter(Boolean))];
          console.log('Found gateway IDs:', gatewayIds);
          
          const gatewayRewards = gatewayIds.map(gwId => {
            const gwName = gateways?.find(g => g.id === gwId)?.name || gwId;
            const gwRewards = data.betaDaily
              .filter(item => item.gwId === gwId)
              .flatMap(item => item.rewards || [])
              .filter(reward => reward.wallet === walletAddress);
            
            const gwTotal = gwRewards.reduce((sum, reward) => sum + reward.amount, 0);
            console.log(`Gateway ${gwId} total rewards:`, gwTotal);
            
            return {
              gwId,
              name: gwName,
              amount: gwTotal
            };
          });

          // Add remaining gateways with zero rewards
          if (gateways) {
            const remainingGateways = gateways.filter(
              gateway => !gatewayIds.includes(gateway.id)
            ).map(gateway => ({
              gwId: gateway.id,
              name: gateway.name,
              amount: 0
            }));

            gatewayRewards.push(...remainingGateways);
          }

          // Sort by rewards amount
          gatewayRewards.sort((a, b) => b.amount - a.amount);
          console.log('Setting gateway rewards:', gatewayRewards);
          setUserGatewayRewards(gatewayRewards);
        }

      } catch (err: any) {
        console.error('Error fetching rewards:', err);
        setError(err?.message || 'Failed to load rewards');
        setTotalRewards(null);
        setCombinedDailyRewards({
          isLoading: false,
          error: 'Failed to load rewards data',
          data: {}
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchWalletData();
  }, [publicKey, gateways, generateSampleData]);

  // Helper functions
  const formatGatewayName = (name: string, gwId: string) => {
    return name || `Gateway ${gwId.slice(0, 6)}...`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Handle chart point click for DER details modal
  const handlePointClick = (data: any) => {
    if (data && data.activePayload && data.activePayload[0] && data.activePayload[0].payload) {
      const payload = data.activePayload[0].payload;
      if (payload.gwId && payload.derDetails) {
        setSelectedGatewayDetails({
          gwId: payload.gwId,
          name: gateways?.find(g => g.id === payload.gwId)?.name || formatGatewayName('', payload.gwId),
          date: payload.date,
          derDetails: payload.derDetails
        });
      }
    }
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      let formattedDate;
      try {
        const date = new Date(label);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        } else {
          formattedDate = label;
        }
      } catch (err) {
        console.error('Error in tooltip formatting date:', label, err);
        formattedDate = label;
      }
      
      // Check if this is sample data
      const isSampleData = payload[0]?.payload?.isSampleData;
      
      return (
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[10px] p-3 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-800 dark:text-white font-medium">{formattedDate}</p>
            {isSampleData && (
              <span className="text-xs text-amber-400 ml-2">Sample</span>
            )}
          </div>
          {payload.map((entry: any, index: number) => (
            <div key={`tooltip-${index}`} className="flex items-center justify-between mb-1 last:mb-0">
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700 dark:text-gray-300 text-sm">{entry.name}</span>
              </div>
              <span className="text-green-600 dark:text-[#00FF84] font-medium text-sm ml-4 font-poly-sans-median">{entry.value.toLocaleString()} Points</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return dateString;
      }
      return date.toLocaleDateString(undefined, { 
        month: 'short', 
        day: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting date:', dateString, err);
      return dateString;
    }
  };

  // Calculate last complete day's rewards and date
  const getLastDayInfo = (data: Record<string, RewardsGraphDataItem[]>): { total: number; date: string } => {
    const now = new Date();
    const lastCompleteDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
    const lastCompleteDayStr = `${lastCompleteDay.toISOString().split('T')[0]}T00:00:00`;
    
    try {
      // If we have total data, use it
      if (data.total) {
        // Get the most recent day's rewards
        const recentRewards = data.total
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 1);

        if (recentRewards.length > 0) {
          return {
            total: recentRewards[0].rewards,
            date: formatDate(recentRewards[0].date)
          };
        }
      }
      
      // Fallback to empty values
      return {
        total: 0,
        date: formatDate(lastCompleteDayStr)
      };
    } catch (err) {
      console.error('Error in getLastDayInfo:', err);
      return {
        total: 0,
        date: formatDate(lastCompleteDayStr)
      };
    }
  };

  // Helper function to get date range for the current month
  const getCurrentMonthRange = (): { start: Date, end: Date } => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
  };

  // Helper function to get date range for the previous month
  const getPreviousMonthRange = (): { start: Date, end: Date } => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start, end };
  };

  // Get filtered rewards based on selected time period
  const getFilteredRewards = (rewards: GatewayReward[], period: TimePeriod): GatewayReward[] => {
    if (period === TimePeriod.ALL_TIME || !publicKey) {
      return rewards;
    }

    // Fetch actual period data from the API
    const walletAddress = publicKey.toString();
    
    // Since we need rewardsData here, we'll use the rewardsDataRef we store during the initial fetch
    if (!rewardsDataRef.current) {
      return rewards; // If no data available yet, return original rewards
    }
    
    if (period === TimePeriod.THIS_MONTH) {
      const { start, end } = getCurrentMonthRange();
      
      // Map each gateway to calculate its rewards for this month
      return rewards.map(reward => {
        // Find this gateway's data in rewardsData
        const gatewayData = rewardsDataRef.current?.betaDaily.filter(g => g.gwId === reward.gwId) || [];
        
        // Calculate total rewards for this gateway in this month
        let periodTotal = 0;
        gatewayData.forEach(gateway => {
          if (gateway.rewards && gateway.rewards.length > 0) {
            gateway.rewards.forEach(r => {
              if (r.wallet === walletAddress) {
                const rewardDate = new Date(r.ts);
                if (rewardDate >= start && rewardDate <= end) {
                  periodTotal += r.amount;
                }
              }
            });
          }
        });
        
        return {
          ...reward,
          amount: periodTotal
        };
      });
    }

    // For LAST_MONTH: actual data from previous month
    if (period === TimePeriod.LAST_MONTH) {
      const { start, end } = getPreviousMonthRange();
      
      // Map each gateway to calculate its rewards for last month
      return rewards.map(reward => {
        // Find this gateway's data in rewardsData
        const gatewayData = rewardsDataRef.current?.betaDaily.filter(g => g.gwId === reward.gwId) || [];
        
        // Calculate total rewards for this gateway in last month
        let periodTotal = 0;
        gatewayData.forEach(gateway => {
          if (gateway.rewards && gateway.rewards.length > 0) {
            gateway.rewards.forEach(r => {
              if (r.wallet === walletAddress) {
                const rewardDate = new Date(r.ts);
                if (rewardDate >= start && rewardDate <= end) {
                  periodTotal += r.amount;
                }
              }
            });
          }
        });
        
        return {
          ...reward,
          amount: periodTotal
        };
      });
    }

    return rewards;
  };

  // Calculate percentage change for display
  const calculatePercentageChange = (gatewayId: string): { value: number; isIncrease: boolean } => {
    if (!publicKey || !rewardsDataRef.current) {
      return { value: 0, isIncrease: true };
    }
    
    const walletAddress = publicKey.toString();
    
    // Get gateway data
    const gatewayData = rewardsDataRef.current.betaDaily.filter(g => g.gwId === gatewayId);
    
    // Calculate this month's rewards
    const thisMonth = getCurrentMonthRange();
    let thisMonthAmount = 0;
    gatewayData.forEach(gateway => {
      if (gateway.rewards && gateway.rewards.length > 0) {
        gateway.rewards.forEach(r => {
          if (r.wallet === walletAddress) {
            const rewardDate = new Date(r.ts);
            if (rewardDate >= thisMonth.start && rewardDate <= thisMonth.end) {
              thisMonthAmount += r.amount;
            }
          }
        });
      }
    });
    
    // Calculate last month's rewards
    const lastMonth = getPreviousMonthRange();
    let lastMonthAmount = 0;
    gatewayData.forEach(gateway => {
      if (gateway.rewards && gateway.rewards.length > 0) {
        gateway.rewards.forEach(r => {
          if (r.wallet === walletAddress) {
            const rewardDate = new Date(r.ts);
            if (rewardDate >= lastMonth.start && rewardDate <= lastMonth.end) {
              lastMonthAmount += r.amount;
            }
          }
        });
      }
    });
    
    // Calculate percentage change
    if (lastMonthAmount === 0) return { value: 0, isIncrease: true }; // Avoid division by zero
    
    const percentChange = Math.round(((thisMonthAmount - lastMonthAmount) / lastMonthAmount) * 100);
    return { 
      value: Math.abs(percentChange), 
      isIncrease: percentChange >= 0 
    };
  };

  return (
    <PageContainer>
      <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
        <div className="flex items-center gap-2 mb-2 sm:mb-4">
          <div className="flex items-center gap-2">
            <BoltIcon className="w-5 h-5 text-green-600 dark:text-[#00FF84]" />
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Your Reward Points</h1>
            <div className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-medium rounded-full">
              Beta
            </div>
          </div>
        </div>
        <p className="text-gray-500 dark:text-gray-400">Track your rewards and see how your gateways are performing</p>
      </div>

      {/* User's rewards summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* All time total card */}
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[8px] p-6 shadow-lg">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-16 h-16 bg-green-100 dark:bg-[#00FF84]/20 rounded-full flex items-center justify-center mb-2">
              <BoltIcon className="w-8 h-8 text-green-600 dark:text-[#00FF84]" />
            </div>
            <h2 className="text-xl font-medium text-gray-800 dark:text-white font-poly-sans-median">All Time Total</h2>
            {isLoading ? (
              <div className="animate-pulse">
                <div className="bg-gray-200 dark:bg-[#292929] h-10 w-40 rounded-[8px]"></div>
              </div>
            ) : !publicKey ? (
              <p className="text-gray-500 dark:text-gray-400">Connect wallet to view</p>
            ) : error ? (
              <p className="text-gray-500 dark:text-gray-400">{error}</p>
            ) : totalRewards !== null ? (
              <p className="text-3xl font-bold text-green-600 dark:text-[#00FF84] font-poly-sans-median">{formatNumber(totalRewards)} Points</p>
            ) : (
              <p className="text-3xl font-bold text-green-600 dark:text-[#00FF84] font-poly-sans-median">0 Points</p>
            )}
          </div>
        </div>

        {/* Recent rewards card */}
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[8px] p-6 shadow-lg">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-16 h-16 bg-green-100 dark:bg-[#00FF84]/20 rounded-full flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-green-600 dark:text-[#00FF84]">
                <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-xl font-medium text-gray-800 dark:text-white mb-4 font-poly-sans-median">Daily Rewards</h2>
            {isLoading ? (
              <div className="animate-pulse flex space-x-4">
                <div className="bg-gray-200 dark:bg-[#292929] h-10 w-32 rounded-[8px]"></div>
                <div className="bg-gray-200 dark:bg-[#292929] h-10 w-32 rounded-[8px]"></div>
              </div>
            ) : combinedDailyRewards.error || Object.keys(combinedDailyRewards.data).length === 0 || !combinedDailyRewards.data.total || combinedDailyRewards.data.total.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No recent rewards data</p>
            ) : (
              <div className="flex flex-col items-center text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-[#00FF84] font-poly-sans-median">
                  {formatNumber(getLastDayInfo(combinedDailyRewards.data).total)} Points
                </div>
                <div className="text-gray-500 dark:text-gray-400 text-sm">
                  on {getLastDayInfo(combinedDailyRewards.data).date}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[8px] p-6 shadow-lg">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-16 h-16 bg-green-100 dark:bg-[#00FF84]/20 rounded-full flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-green-600 dark:text-[#00FF84]">
                <path d="M11.584 2.376a.75.75 0 01.832 0l9 6a.75.75 0 11-.832 1.248L12 3.901 3.416 9.624a.75.75 0 01-.832-1.248l9-6z" />
                <path fillRule="evenodd" d="M20.25 10.332v9.918H21a.75.75 0 010 1.5H3a.75.75 0 010-1.5h.75v-9.918a.75.75 0 01.634-.74A49.109 49.109 0 0112 9c2.59 0 5.134.202 7.616.592a.75.75 0 01.634.74zm-7.5 2.418a.75.75 0 00-1.5 0v6.75a.75.75 0 001.5 0v-6.75zm3-.75a.75.75 0 01.75.75v6.75a.75.75 0 01-1.5 0v-6.75a.75.75 0 01.75-.75zM9 12.75a.75.75 0 00-1.5 0v6.75a.75.75 0 001.5 0v-6.75z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-xl font-medium text-gray-800 dark:text-white font-poly-sans-median">Your Gateways</h2>
            {isLoading ? (
              <div className="animate-pulse">
                <div className="bg-gray-200 dark:bg-[#292929] h-10 w-40 rounded-[8px]"></div>
              </div>
            ) : !publicKey ? (
              <p className="text-gray-500 dark:text-gray-400">Connect wallet to view</p>
            ) : (
              <div className="text-center">
                <p className="text-4xl font-bold text-green-600 dark:text-[#00FF84] font-poly-sans-median">{userGatewayRewards.length}</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">earning rewards</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User's gateway rewards */}
      <div className="mb-6">
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[8px] p-6 shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-medium text-gray-800 dark:text-white">Daily Rewards History</h2>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">Last 30 days</p>
              {sampleDataRef.current && sampleDataRef.current.length > 0 && 
              sampleDataRef.current[0].isSampleData && (
                <div className="px-3 py-1 bg-amber-600/20 text-amber-400 text-xs font-medium rounded-full">
                  Sample Data
                </div>
              )}
            </div>
          </div>

          {/* Debug info - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div className="hidden">
              <p>Debug Chart Data: {JSON.stringify(sampleDataRef.current?.length)} points</p>
              <pre>{JSON.stringify(sampleDataRef.current?.slice(0, 3), null, 2)}</pre>
            </div>
          )}

          {isLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="bg-gray-200 dark:bg-[#292929] h-64 w-full rounded-[8px]"></div>
            </div>
          ) : !publicKey ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">Connect wallet to view your gateway rewards</p>
          ) : userGatewayRewards.length === 0 && (!sampleDataRef.current || sampleDataRef.current.length === 0) ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No gateway rewards found for this time period</p>
              <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">Your gateways may not be earning rewards yet or may not be in the top performers list</p>
            </div>
          ) : (
            <>
              {/* Daily rewards chart */}
              <div className="h-64 mb-6">
                {sampleDataRef.current && sampleDataRef.current.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={[...sampleDataRef.current].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())}
                      margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
                      onClick={handlePointClick}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={true} />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => {
                          try {
                            if (!date) return '';
                            const d = new Date(date);
                            if (isNaN(d.getTime())) return '';
                            return `${d.getDate()}/${d.getMonth() + 1}`;
                          } catch (err) {
                            console.error('Error formatting date:', date, err);
                            return '';
                          }
                        }}
                        interval="preserveEnd"
                        tickCount={7}
                        stroke="#a3a3a3"
                      />
                      <YAxis 
                        stroke="#a3a3a3" 
                        tickFormatter={(value) => value.toLocaleString()}
                        domain={['dataMin', 'auto']}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line 
                        type="monotone" 
                        dataKey="rewards" 
                        stroke="#00FF84" 
                        activeDot={{ r: 6, fill: "#00FF84", stroke: "#00FF84" }}
                        dot={{ r: 3, fill: "#00FF84", strokeWidth: 0 }}
                        strokeWidth={2}
                        name="Daily Rewards"
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 dark:text-gray-400">No daily rewards data available</p>
                  </div>
                )}
              </div>
              
              {/* Divider between chart and gateway breakdown */}
              <div className="border-t border-gray-200 dark:border-[#292929] my-8"></div>
              
              {/* Gateway rewards breakdown */}
              <div className="mt-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-16 md:mb-0">Your Gateway Rewards Breakdown</h3>
                  <div className="flex w-full md:w-auto pt-2 md:pt-0">
                    <button 
                      className={`flex-1 md:flex-none px-2 md:px-4 py-1.5 text-xs md:text-sm rounded-l-[20px] transition ${selectedTimePeriod === TimePeriod.ALL_TIME ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                      onClick={() => setSelectedTimePeriod(TimePeriod.ALL_TIME)}
                    >
                      All Time
                    </button>
                    <button 
                      className={`flex-1 md:flex-none px-2 md:px-4 py-1.5 text-xs md:text-sm transition ${selectedTimePeriod === TimePeriod.THIS_MONTH ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                      onClick={() => setSelectedTimePeriod(TimePeriod.THIS_MONTH)}
                    >
                      This Month
                    </button>
                    <button 
                      className={`flex-1 md:flex-none px-2 md:px-4 py-1.5 text-xs md:text-sm rounded-r-[20px] transition ${selectedTimePeriod === TimePeriod.LAST_MONTH ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                      onClick={() => setSelectedTimePeriod(TimePeriod.LAST_MONTH)}
                    >
                      Last Month
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getFilteredRewards(userGatewayRewards, selectedTimePeriod).filter(gw => gw.amount > 0).map((gateway, index) => {
                    // Only calculate percentage change for THIS_MONTH view
                    const showPercentage = selectedTimePeriod === TimePeriod.THIS_MONTH;
                    const percentageChange = showPercentage ? calculatePercentageChange(gateway.gwId) : null;
                    
                    return (
                      <div key={gateway.gwId} className="bg-gray-100 dark:bg-[#292929]/50 rounded-[10px] p-4 hover:bg-gray-200 dark:hover:bg-[#292929] transition-colors">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium text-gray-800 dark:text-white">{formatGatewayName(gateway.name, gateway.gwId)}</h4>
                          {showPercentage && percentageChange && (
                            <div className={`flex items-center text-sm ${percentageChange.isIncrease ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                              {percentageChange.isIncrease ? (
                                <ArrowUpIcon className="w-4 h-4 mr-1" />
                              ) : (
                                <ArrowDownIcon className="w-4 h-4 mr-1" />
                              )}
                              {percentageChange.value}%
                            </div>
                          )}
                        </div>
                        <p className="text-2xl font-bold text-green-600 dark:text-[#00FF84] font-poly-sans-median">{formatNumber(gateway.amount)} Points</p>
                      </div>
                    );
                  })}
                  {getFilteredRewards(userGatewayRewards, selectedTimePeriod).filter(gw => gw.amount > 0).length === 0 && (
                    <div className="col-span-3 text-center py-4">
                      <p className="text-gray-500 dark:text-gray-400">No gateways with rewards found for this time period</p>
                    </div>
                  )}
                </div>
              </div>
              
              {selectedGatewayDetails && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[8px] p-6 max-w-lg w-full mx-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200">
                        {formatGatewayName(selectedGatewayDetails.name, selectedGatewayDetails.gwId)}
                      </h3>
                      <button
                        onClick={() => setSelectedGatewayDetails(null)}
                        className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      DER Rewards for {formatDate(selectedGatewayDetails.date)}
                    </p>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {Object.entries(selectedGatewayDetails.derDetails).map(([sn, amount]) => (
                        <div key={sn} className="flex justify-between items-center p-3 bg-gray-100 dark:bg-[#292929]/50 rounded-[10px]">
                          <div className="font-mono text-gray-700 dark:text-gray-300">{sn}</div>
                          <div className="text-green-600 dark:text-[#00FF84] font-medium font-poly-sans-median">{formatNumber(amount)} Points</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={() => setSelectedGatewayDetails(null)}
                        className="px-4 py-2 bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939] rounded-[10px] transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Info box about rewards calculation */}
      <div className="mb-6">
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[8px] p-6 shadow-lg">
          <h2 className="text-xl font-medium text-gray-800 dark:text-white mb-4">How Rewards are Calculated</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4 mt-4">
            The Sourceful DER Network points system is designed to incentivize participation, ensure data quality, and promote network growth through a carefully balanced reward mechanism.
          </p>
          <a 
            href="https://docs.sourceful.energy/tokenomics/framework" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-green-600 dark:text-[#00FF84] hover:text-green-700 dark:hover:text-[#00CC66] flex items-center transition-colors"
          >
            Read more
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 ml-1">
              <path fillRule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </div>
    </PageContainer>
  );
};

export default RewardsPage; 